import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axiosConfig';
import { ChevronDown, Clock, User, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import './PresentationScreen.css';

const API = '/api';

const PresentationScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [particles, setParticles] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [estacoes, setEstacoes] = useState([]);
  const [medicosOnline, setMedicosOnline] = useState([]);
  const [convitesPendentes, setConvitesPendentes] = useState([]);
  const [selectedEspecialidade, setSelectedEspecialidade] = useState(null);
  const [selectedEstacao, setSelectedEstacao] = useState(null);
  const [showEstacoesDialog, setShowEstacoesDialog] = useState(false);
  const [showMedicosDialog, setShowMedicosDialog] = useState(false);
  const [showPapelDialog, setShowPapelDialog] = useState(false);
  const [showConviteDialog, setShowConviteDialog] = useState(false);
  const [selectedMedico, setSelectedMedico] = useState(null);
  const [selectedPapel, setSelectedPapel] = useState(null);
  const [conviteAtual, setConviteAtual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalMensagens, setTotalMensagens] = useState(0);

  // Fixed specialties according to requirements
  const especialidadesFixas = [
    { id: 'clinica', nome: 'Clínica Médica', icone: '⚕️', imagem: '/clinica-medica.png' },
    { id: 'cirurgia', nome: 'Cirurgia', icone: '🏥', imagem: '/cirurgia.png' },
    { id: 'pediatria', nome: 'Pediatria', icone: '👶', imagem: '/pediatria.png' },
    { id: 'preventiva', nome: 'Preventiva', icone: '🛡️', imagem: '/preventiva.png' },
    { id: 'gineco', nome: 'Ginecologia e Obstetrícia', icone: '🤰', imagem: '/ginecologia.png' },
    { id: 'resumos', nome: 'Resumos', icone: '📚', imagem: '/resumos.png' }
  ];

  useEffect(() => {
    // Generate 40 particles with random properties (reduced from 150 for desktop performance)
    const newParticles = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: Math.random() * 4 + 1,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);

    fetchEspecialidades();
    fetchConvites();
    fetchMensagensContador();
    // Poll for new invites every 5 seconds
    const interval = setInterval(fetchConvites, 5000);
    return () => clearInterval(interval);
  }, []);

  // NEW: Listen to WebSocket convite_novo events
  useEffect(() => {
    console.log('[PresentationScreen] Registrando listener para convite_novo_ws');
    
    const handleConviteNovoWS = (event) => {
      const data = event.detail;
      console.log('[PresentationScreen] 🎯 Evento convite_novo_ws recebido:', data);
      
      // Show modal immediately (instead of waiting for polling)
      setConviteAtual({
        id: data.sessaoId,
        criadorNome: data.remetenteNome,
        estacaoNome: data.estacaoNome,
        papel: data.papel
      });
      setShowConviteDialog(true);
      console.log('[PresentationScreen] ✅ Modal de convite aberto');
      
      // Also refresh the list
      fetchConvites();
    };

    window.addEventListener('convite_novo_ws', handleConviteNovoWS);

    return () => {
      window.removeEventListener('convite_novo_ws', handleConviteNovoWS);
    };
  }, []);

  const fetchMensagensContador = async () => {
    try {
      const response = await axios.get(`${API}/mensagens-suporte/contador`);
      setTotalMensagens(response.data.totalMensagens || 0);
    } catch (error) {
      console.error('Erro ao buscar contador de mensagens:', error);
    }
  };

  const fetchEspecialidades = async () => {
    try {
      console.log('[ESPECIALIDADES] Fetching from:', `${API}/especialidades`);
      const response = await axios.get(`${API}/especialidades`);
      console.log('[ESPECIALIDADES] Success! Count:', response.data.length);
      setEspecialidades(response.data);
    } catch (error) {
      console.error('[ESPECIALIDADES] Error fetching:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        endpoint: `${API}/especialidades`
      });
      toast.error('Erro ao carregar especialidades');
    } finally {
      setLoading(false);
    }
  };

  const fetchConvites = async () => {
    try {
      const response = await axios.get(`${API}/sessoes/convites`);
      setConvitesPendentes(response.data);
      if (response.data.length > 0 && !showConviteDialog) {
        setConviteAtual(response.data[0]);
        setShowConviteDialog(true);
      }
    } catch (error) {
      console.error('Erro ao buscar convites:', error);
    }
  };

  const handleEspecialidadeClick = async (especialidade) => {
    // Special handling for Resumos
    if (especialidade.id === 'resumos') {
      navigate('/usuario/resumos');
      return;
    }

    // Find matching real especialidade from database
    const especialidadeReal = especialidades.find(esp => 
      esp.nome.toLowerCase().includes(especialidade.nome.toLowerCase().split(' ')[0])
    );

    if (!especialidadeReal) {
      toast.error('Especialidade não encontrada');
      return;
    }

    try {
      const response = await axios.get(`${API}/estacoes?especialidadeId=${especialidadeReal.id}`);
      setEstacoes(response.data);
      setSelectedEspecialidade(especialidadeReal);
      setShowEstacoesDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar estações');
    }
  };

  const handleEstacaoSelect = async (estacao) => {
    setSelectedEstacao(estacao);
    setShowEstacoesDialog(false);
    
    // Load online medicos
    try {
      const response = await axios.get(`${API}/medicos/online`);
      setMedicosOnline(response.data);
      setShowMedicosDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar médicos online');
    }
  };

  const handleMedicoSelect = (medico) => {
    setSelectedMedico(medico);
    setShowMedicosDialog(false);
    setShowPapelDialog(true);
  };

  const handlePapelSelect = async (papel) => {
    setSelectedPapel(papel);
    setShowPapelDialog(false);
    
    // Create session
    try {
      const response = await axios.post(`${API}/sessoes`, {
        estacaoId: selectedEstacao.id,
        convidadoId: selectedMedico.id,
        papelCriador: papel
      });
      
      toast.success(`Convite enviado para ${selectedMedico.nome}`);
      
      // Navigate to waiting page
      navigate(`/usuario/sessao/${response.data.id}`);
    } catch (error) {
      toast.error('Erro ao criar sessão');
    }
  };

  // NEW: Handle random station generation
  const handleGerarEstacaoAleatoria = async () => {
    try {
      // Load all stations from all specialties
      const todasEstacoes = [];
      
      for (const especialidadeFixa of especialidadesFixas) {
        // Skip Resumos
        if (especialidadeFixa.id === 'resumos') continue;
        
        // Find matching real especialidade
        const especialidadeReal = especialidades.find(esp => 
          esp.nome.toLowerCase().includes(especialidadeFixa.nome.toLowerCase().split(' ')[0])
        );
        
        if (especialidadeReal) {
          const response = await axios.get(`${API}/estacoes?especialidadeId=${especialidadeReal.id}`);
          todasEstacoes.push(...response.data);
        }
      }
      
      // Check if there are stations available
      if (todasEstacoes.length === 0) {
        toast.error('Nenhuma estação disponível para sorteio');
        return;
      }
      
      // Pick a random station
      const randomIndex = Math.floor(Math.random() * todasEstacoes.length);
      const estacaoSorteada = todasEstacoes[randomIndex];
      
      toast.success(`Estação sorteada: ${estacaoSorteada.nome}`);
      
      // Follow the same flow as manual selection
      setSelectedEstacao(estacaoSorteada);
      
      // Load online medicos
      const medicosResponse = await axios.get(`${API}/medicos/online`);
      setMedicosOnline(medicosResponse.data);
      setShowMedicosDialog(true);
    } catch (error) {
      console.error('Erro ao gerar estação aleatória:', error);
      toast.error('Erro ao gerar estação aleatória');
    }
  };

  const handleRespondConvite = async (aceitar) => {
    try {
      await axios.put(`${API}/sessoes/${conviteAtual.id}/responder`, {
        aceitar
      });
      
      if (aceitar) {
        toast.success('Convite aceito! Entrando na sessão...');
        navigate(`/usuario/sessao/${conviteAtual.id}`);
      } else {
        toast.info('Convite recusado');
        setShowConviteDialog(false);
        setConviteAtual(null);
        fetchConvites();
      }
    } catch (error) {
      toast.error('Erro ao responder convite');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="presentation-screen">
        <div className="content-wrapper">
          <div className="text-white text-xl">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="presentation-screen">
      {/* Grain Texture Overlay */}
      <div className="grain-overlay" />

      {/* Animated Orbs */}
      <div className="orbs-container">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>

      {/* Fluid Waves */}
      <div className="waves-container">
        <div className="wave wave-1" />
        <div className="wave wave-2" />
        <div className="wave wave-3" />
      </div>

      {/* Floating Particles */}
      <div className="particles-container">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.left}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}
      </div>

      {/* Header with navigation */}
      <div className="presentation-header">
        <div className="header-content">
          <div className="header-logo">
            <img src="/logo.png" alt="ClinicSim Logo" className="logo-image" />
          </div>
          <div className="header-actions">
            {convitesPendentes.length > 0 && (
              <div className="notification-badge">
                <Bell className="w-6 h-6 text-[#CF0D1D] animate-pulse" />
                <span className="notification-count">
                  {convitesPendentes.length}
                </span>
              </div>
            )}
            <Button
              onClick={() => navigate('/usuario/suporte')}
              variant="outline"
              className="header-button relative"
            >
              Suporte
              {totalMensagens > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#ff2a2a] text-white text-xs font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                  {totalMensagens}
                </span>
              )}
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="header-button-logout"
            >
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          {/* Title */}
          <h1 className="hero-title">
            <span className="title-line-1">Bem-vindo ao método RB</span>
            <span className="title-line-2">
              Dr(a) {user?.nome || 'Usuário'}
            </span>
          </h1>

          {/* Description */}
          <p className="hero-description">
            Não importa quantas vezes você tentou — o que importa é continuar.<br />
            Aqui, cada sessão é uma nova chance de chegar mais perto do seu sonho
          </p>

          {/* Scroll Indicator */}
          <div className="scroll-indicator">
            <div className="scroll-line" />
            <span className="scroll-text">ROLE PARA EXPLORAR</span>
            <ChevronDown className="scroll-icon" size={20} />
          </div>
        </div>
      </div>

      {/* Specialties Section */}
      <div className="specialties-section">
        <div className="specialties-container">
          <div className="section-header">
            <h2 className="section-title">Escolha uma Especialidade</h2>
            <p className="section-description">Selecione uma especialidade para iniciar uma sessão de treinamento</p>
          </div>

          <div className="specialties-grid">
            {/* NEW: Random Station Card - First card, only for Médicos */}
            {user?.papel === 'medico' && (
              <Card
                className="specialty-card-image"
                onClick={handleGerarEstacaoAleatoria}
                data-testid="especialidade-card-aleatoria"
                style={{
                  background: 'linear-gradient(135deg, #8B0000, #FF3B3B)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <div 
                  className="image-card-overlay"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "100%",
                    padding: "16px 18px",
                  }}
                >
                  {/* BLOCO DO DADO - CENTRALIZADO NO TOPO */}
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "32px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "70px",
                        textShadow: "0 4px 12px rgba(0,0,0,0.45)",
                        display: "block"
                      }}
                    >
                      🎲
                    </span>
                  </div>
                  
                  {/* RODAPÉ - APENAS TEXTO, SEM DADO */}
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <h3 className="image-card-title" style={{ margin: 0 }}>Estação Aleatória</h3>
                  </div>
                </div>
              </Card>
            )}
            
            {especialidadesFixas.map((especialidade) => (
              <Card
                key={especialidade.id}
                className="specialty-card-image"
                onClick={() => handleEspecialidadeClick(especialidade)}
                data-testid={`especialidade-card-${especialidade.id}`}
                style={{
                  backgroundImage: `url(${especialidade.imagem})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <div className="image-card-overlay">
                  <h3 className="image-card-title">{especialidade.nome}</h3>
                </div>
              </Card>
            ))}
          </div>

          {/* NEW: Desempenho Card - Only for Médicos */}
          {user?.papel === 'medico' && (
            <div style={{
              maxWidth: '1200px',
              width: '100%',
              margin: '0 auto',
              marginTop: '48px'
            }}>
              <Card
                className="desempenho-banner-card"
                onClick={() => navigate('/medico/desempenho')}
                style={{
                  width: '100%',
                  minHeight: window.innerWidth < 768 ? '140px' : '160px',
                  marginTop: '20px',
                  marginBottom: '24px',
                  cursor: 'pointer',
                  backgroundImage: 'url(https://customer-assets.emergentagent.com/job_medeval-dashboard/artifacts/ov0zp3la_Meu%20Desempenho%20%280-00-00-29%29.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  border: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
              {/* Dark overlay for text legibility */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '12px',
                zIndex: 1
              }} />
              
              {/* Content with higher z-index */}
              <div style={{ 
                padding: '24px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 2
              }}>
                <h3 style={{
                  fontSize: window.innerWidth < 768 ? '1.5rem' : '1.75rem',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '8px',
                  letterSpacing: '-0.02em'
                }}>
                  Meu Desempenho
                </h3>
                <p style={{
                  fontSize: '0.95rem',
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontWeight: '400'
                }}>
                  Acompanhe sua evolução nas estações
                </p>
              </div>
            </Card>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de Estações */}
      <Dialog open={showEstacoesDialog} onOpenChange={setShowEstacoesDialog}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="dialog-title">
              {selectedEspecialidade?.nome}
            </DialogTitle>
            <DialogDescription className="dialog-description">
              Selecione uma estação para iniciar sua sessão
            </DialogDescription>
          </DialogHeader>
          <div className="dialog-list">
            {estacoes.length === 0 ? (
              <p className="dialog-empty">Nenhuma estação disponível</p>
            ) : (
              estacoes.map((estacao) => (
                <div
                  key={estacao.id}
                  className="dialog-item"
                  onClick={() => handleEstacaoSelect(estacao)}
                  data-testid={`estacao-item-${estacao.id}`}
                >
                  <div className="dialog-item-content">
                    <h3 className="dialog-item-title">{estacao.nome}</h3>
                    <p className="dialog-item-description">{estacao.descricaoCurta}</p>
                    <div className="dialog-item-meta">
                      <Clock className="w-4 h-4" />
                      <span>Tempo máximo: {estacao.tempoMaximoMinutos} minutos</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Médicos Online */}
      <Dialog open={showMedicosDialog} onOpenChange={setShowMedicosDialog}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="dialog-title">Médicos Online</DialogTitle>
            <DialogDescription className="dialog-description">
              Selecione um médico para convidar
            </DialogDescription>
          </DialogHeader>
          <div className="dialog-list">
            {medicosOnline.length === 0 ? (
              <p className="dialog-empty">Nenhum médico online</p>
            ) : (
              medicosOnline.map((medico) => (
                <div
                  key={medico.id}
                  className="dialog-item"
                  onClick={() => handleMedicoSelect(medico)}
                  data-testid={`medico-${medico.id}`}
                >
                  <div className="dialog-item-medico">
                    <div className="medico-avatar">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="dialog-item-title">{medico.nome}</h3>
                      <p className="dialog-item-email">{medico.email}</p>
                    </div>
                    <div className="medico-status">
                      <div className="status-dot"></div>
                      Online
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Escolher Papel */}
      <Dialog open={showPapelDialog} onOpenChange={setShowPapelDialog}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="dialog-title">Escolha seu Papel</DialogTitle>
            <DialogDescription className="dialog-description">
              Você será o médico ou o ator nesta sessão?
            </DialogDescription>
          </DialogHeader>
          <div className="papel-grid">
            <Card
              className="papel-card"
              onClick={() => handlePapelSelect('medico')}
              data-testid="papel-medico"
            >
              <CardContent className="papel-content">
                <div className="papel-icon">⚕️</div>
                <h3 className="papel-title">Médico</h3>
                <p className="papel-description">Você conduzirá a anamnese</p>
              </CardContent>
            </Card>
            <Card
              className="papel-card"
              onClick={() => handlePapelSelect('ator')}
              data-testid="papel-ator"
            >
              <CardContent className="papel-content">
                <div className="papel-icon">🎭</div>
                <h3 className="papel-title">Ator</h3>
                <p className="papel-description">Você interpretará o paciente</p>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Convite Recebido */}
      {conviteAtual && (
        <Dialog open={showConviteDialog} onOpenChange={setShowConviteDialog}>
          <DialogContent className="dialog-content modal-convite">
            <DialogHeader>
              <DialogTitle className="dialog-title modal-convite__title">
                Novo convite de sessão
              </DialogTitle>
              <DialogDescription className="dialog-description modal-convite__subtitle">
                {conviteAtual.criadorNome && (
                  <>
                    Dr(a) <strong>{conviteAtual.criadorNome}</strong> convidou você para uma sessão.
                  </>
                )}
                {!conviteAtual.criadorNome && (
                  <>Você foi convidado para uma sessão.</>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="modal-convite__card">
              <span className="modal-convite__card-label">Você entrará como:</span>
              <div className="modal-convite__role-badge">
                <span className="modal-convite__role-emoji">
                  {(conviteAtual.papel === 'medico' || conviteAtual.papelConvidado === 'medico') ? '⚕️' : '🎭'}
                </span>
                <span className="modal-convite__role-text">
                  {(conviteAtual.papel === 'medico' || conviteAtual.papelConvidado === 'medico') ? 'Médico' : 'Ator'}
                </span>
              </div>
            </div>
            
            <DialogFooter className="modal-convite__actions">
              <Button
                variant="outline"
                onClick={() => handleRespondConvite(false)}
                className="convite-button-secondary"
              >
                Recusar
              </Button>
              <Button
                onClick={() => handleRespondConvite(true)}
                className="convite-button-primary"
                data-testid="aceitar-convite"
              >
                Aceitar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PresentationScreen;
